import { jwtDecode } from "jwt-decode";
import { v4 as uuidv4 } from "uuid";
import i18n from "../i18n";
import {
  AI_CHAT_LOCAL_STORAGE_KEY,
  DEVICE_ID_KEY,
  KEY_SORT_NEWEST,
  KEY_SORT_PRICE_ASC,
  KEY_SORT_PRICE_DESC,
  KEY_SORT_RATING,
  KEY_SORT_RELEVANCE,
  KEY_URL_AUCTION_ID,
  KEY_URL_AVAILABLE,
  KEY_URL_BID_TYPE,
  KEY_URL_CATEGORY,
  KEY_URL_COURIER,
  KEY_URL_DATE_MAX,
  KEY_URL_DATE_MIN,
  KEY_URL_DAYS_LEFT,
  KEY_URL_PAID,
  KEY_URL_PRICE_MAX,
  KEY_URL_PRICE_MIN,
  KEY_URL_RATING,
  KEY_URL_SEARCH,
  KEY_URL_SORT,
  KEY_URL_STATUS,
  KEY_URL_TRACKING,
  KEY_URL_WON_BY,
  MIN_ALLOWED_TIME_LEFT
} from "../utilities/constants";

export function generateTimeRemainingString(timeRemaining: number): string {
  // gets the time remaining in seconds and returns a string in the format "hh:mm:ss"

  if (timeRemaining <= MIN_ALLOWED_TIME_LEFT) {
    // return i18n.t("SOLD!");
    return "00:00:00";
  }

  if (timeRemaining <= 0) {
    return "00:00:00";
  }

  const min = 60;
  const hour = min * 60;
  const day = hour * 24;

  const days = Math.floor(timeRemaining / day);
  const hours = Math.floor((timeRemaining % day) / hour);
  const minutes = Math.floor((timeRemaining % hour) / min);
  const seconds = Math.floor(timeRemaining % min);

  const hoursString = hours < 10 ? `0${hours}` : `${hours}`;
  const minutesString = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const secondsString = seconds < 10 ? `0${seconds}` : `${seconds}`;

  if (days > 0) {
    return `${days}d ${hoursString}:${minutesString}:${secondsString}`;
  } else {
    return `${hoursString}:${minutesString}:${secondsString}`;
  }
}

export function getUserLanguage() {
  let lang = localStorage.getItem("i18nextLng") || "en";
  // remove the region code
  // lang = lang.split("-")[0];
  return lang;
}

export function displayDate(date: string): string {
  // if the day is today, return "HH:MM:SS"
  // if the day is yesterday, return "Yesterday at HH:MM:SS"
  // if the day is before yesterday, return "DD/MM/YYYY at HH:MM:SS"
  try {
    const dateObj = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const timeOptions = {
      hour12: false,
      hour: "2-digit" as "2-digit",
      minute: "2-digit" as "2-digit",
      second: "2-digit" as "2-digit"
    };

    if (dateObj.toDateString() === today.toDateString()) {
      return `${dateObj.toLocaleTimeString(undefined, timeOptions)}`;
    } else if (dateObj.toDateString() === yesterday.toDateString()) {
      return `${i18n.t("Yesterday")} ${dateObj.toLocaleTimeString(undefined, timeOptions)}`;
    } else {
      return `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()} ${dateObj.toLocaleTimeString(undefined, timeOptions)}`;
    }
  } catch (error) {
    console.error("Error in displayDate function", error);
    return date;
  }
}

export function displayDateWinnings(date: string): string {
  // if the day is today, return "HH:MM:SS"
  // if the day is yesterday, return "Yesterday at HH:MM:SS"
  // if the day is before yesterday, return "DD/MM/YYYY at HH:MM:SS"
  try {
    console.log("date", date);
    const dateObj = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const timeOptions = {
      hour12: false,
      hour: "2-digit" as "2-digit",
      minute: "2-digit" as "2-digit",
      second: "2-digit" as "2-digit"
    };

    if (dateObj.toDateString() === today.toDateString()) {
      return `${dateObj.toLocaleTimeString(undefined, timeOptions)}`;
    } else if (dateObj.toDateString() === yesterday.toDateString()) {
      return `${i18n.t("Yesterday")} `;
    } else {
      const month = dateObj.toLocaleString("default", { month: "short" });
      return `${dateObj.getDate()} ${month} ${dateObj.getFullYear()}`;
    }
  } catch (error) {
    console.error("Error in displayDate function", error);
    return date;
  }
}

export function decodeUserJWT(token: unknown): DecodedUserJWT | null {
  if (typeof token !== "string") {
    console.log("Token is not a string");
    return null;
  }
  try {
    const data: object = jwtDecode(token);
    // console.log(data, "data");
    if (!data || typeof data !== "object") {
      console.log("Data is not an object");
      return null;
    }

    // check expirations
    const now = Date.now() / 1000;
    if (!("exp" in data) || typeof data.exp !== "number" || data.exp < now) {
      console.log("Token has expired");
      console.log(
        "Token expired so long ago",
        "exp" in data && typeof data.exp === "number"
          ? data.exp - now
          : "no exp field"
      );
      return null;
    }

    console.log("###tokenData###", data);

    if (
      !("uuid" in data) ||
      !("email" in data) ||
      !("nickname" in data) ||
      !("bidToken" in data)
    ) {
      console.log("Data is missing required fields");
      return null;
    }

    if (
      typeof data.uuid !== "string" ||
      typeof data.email !== "string" ||
      typeof data.nickname !== "string" ||
      typeof data.bidToken !== "string" ||
      ("lastCheckedIn" in data && typeof data.lastCheckedIn !== "string") ||
      ("mobileNumber" in data && typeof data.mobileNumber !== "string")
    ) {
      console.log("Data fields have incorrect types");
      return null;
    }

    console.log("Decoded user JWT", data);

    const uuid = data.uuid;
    const email = data.email;
    const nickname = data.nickname;
    const bidToken = data.bidToken;
    const lastCheckedIn =
      "lastCheckedIn" in data ? data.lastCheckedIn : undefined;
    const mobileNumber = "mobileNumber" in data ? data.mobileNumber : undefined;

    return {
      uuid,
      email,
      nickname,
      bidToken,
      lastCheckedIn: lastCheckedIn as string | undefined,
      mobileNumber: mobileNumber as string | ""
    };
  } catch (error) {
    console.error("Error in decodeUserJWT function", error);
    return null;
  }
}

export function JWTIsExpired(token: unknown): boolean {
  if (typeof token !== "string") {
    console.log("Token is not a string");
    return true;
  }
  try {
    const data = jwtDecode(token);
    console.log(data, "data");
    if (!data || typeof data !== "object") {
      console.log("Data is not an object");
      return true;
    }

    // check expirations
    const now = Date.now() / 1000;
    if (!("exp" in data) || typeof data.exp !== "number" || data.exp < now) {
      console.log("Token has expired");
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error in JWTIsExpired function", error);
    return true;
  }
}

export function JWTNotGoingToExpire(
  token: unknown,
  maxTimeSeconds: number
): boolean {
  if (typeof token !== "string") {
    console.log("Token is not a string");
    return false;
  }
  try {
    const data = jwtDecode(token);
    console.log(data, "data");
    if (!data || typeof data !== "object") {
      console.log("Data is not an object");
      return false;
    }

    if (!("exp" in data) || typeof data.exp !== "number") {
      console.log("Data is missing required fields");
      return false;
    }

    // check expirations
    const now = Date.now() / 1000;
    if (data.exp < now) {
      console.log("Token has expired");
      return false;
    }

    const timeRemaining = data.exp - now;
    console.log("Time remaining", timeRemaining);

    if (timeRemaining < maxTimeSeconds) {
      console.log("Token is going to expire soon");
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error in JWTNotGoingToExpire function", error);
    return false;
  }
}

// Utility function to create a thumbnail from a image
export async function createThumbnail(
  file: File,
  size: number
): Promise<string | false> {
  return new Promise((resolve, reject) => {
    // Check if the file type is allowed
    if (!file.type.match(/image\/(png|jpg|jpeg|webp|svg\+xml|gif|tiff)/)) {
      resolve(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageUrl = reader.result as string;

      const img = new Image();
      img.src = imageUrl;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (ctx) {
          let width = img.width;
          let height = img.height;
          const maxSide = Math.max(width, height);

          if (maxSide > size) {
            const scaleFactor = size / maxSide;
            width *= scaleFactor;
            height *= scaleFactor;
          }

          canvas.width = width;
          canvas.height = height;

          ctx.drawImage(img, 0, 0, width, height);
          const resizedImageUrl = canvas.toDataURL("image/webp");
          resolve(resizedImageUrl);
        } else {
          resolve(false);
        }
      };
    };

    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      resolve(false);
    };

    reader.readAsDataURL(file);
  });
}

// Utility function to check the user's local time is in sync with the server time. Takes in the server time in ms since epoch and returns a boolean
export function checkTimeSync(serverTime: number): boolean {
  const maxDiff = getMilliseconds(
    { d: 0, h: 0, m: 0, s: 5 } // 5 seconds
  );
  const now = Date.now();
  const diff = now - serverTime * 1000;
  const isOk = Math.abs(diff) < maxDiff;
  if (!isOk) {
    console.error("Time is not in sync");
    console.log("Now", now);
    console.log("Server time", serverTime * 1000);
    console.log("Time difference", diff);
  }

  return isOk;
}

// Utility function to get the milliseconds from the days, hours, minutes, and seconds
export function getMilliseconds({
  d,
  h,
  m,
  s
}: {
  d: number;
  h: number;
  m: number;
  s: number;
}): number {
  return (d * 24 * 60 * 60 + h * 60 * 60 + m * 60 + s) * 1000;
}

export function getNumPrice(price: string): number {
  try {
    // allow .
    const numPrice = parseFloat(price.replace(/[^0-9.]/g, ""));
    // console.log("numPrice", numPrice);
    return numPrice;
  } catch (e) {
    console.log(e);
    return 0;
  }
}

// sort prices SearchFilterOptions['price']
export function sortFilterOptionsPrice(price: {
  [key: string]: number;
}): Map<string, number> {
  // convert all keys to numbers and sort, but keep track of original keys
  const sorted = Object.keys(price)
    .map((key) => [getNumPrice(key), key] as [number, string])
    .sort((a, b) => a[0] - b[0]);

  // console.log("sorted", sorted);

  // create the new map
  const newMap = new Map<string, number>();
  // console.log("price", price);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const [_, originalKey] of sorted) {
    newMap.set(originalKey as string, price[originalKey]);
  }

  // console.log("newMaps", newMap);

  return newMap;
}

export function sortShortProducts(
  products: Readonly<ShortProduct[]>,
  sort: string
): ShortProduct[] {
  // console.log("genericSort");
  // console.log(sort);
  switch (sort) {
    case "relevance":
      return [...products];
    case "date":
      return [...products].sort((a, b) => {
        return (
          new Date(b.auctionTime).getTime() - new Date(a.auctionTime).getTime()
        );
      });
    case "priceAsc":
      return [...products].sort(
        (a, b) =>
          getNumPrice(
            a.lastPlacedBid ? a.lastPlacedBid.bidAmount : a.startingAmount
          ) -
          getNumPrice(
            b.lastPlacedBid ? b.lastPlacedBid.bidAmount : b.startingAmount
          )
      );
    case "priceDesc":
      return [...products].sort(
        (a, b) =>
          getNumPrice(
            b.lastPlacedBid ? b.lastPlacedBid.bidAmount : b.startingAmount
          ) -
          getNumPrice(
            a.lastPlacedBid ? a.lastPlacedBid.bidAmount : a.startingAmount
          )
      );
    // case "rating":
    //   return [...products].sort((a, b) => b.rating - a.rating);

    default:
      return [...products];
  }
}

export function recentCheckIn(): Boolean {
  const lastCheckIn = localStorage.getItem("lastCheckIn");
  if (!lastCheckIn) {
    return false;
  }
  const now = Date.now();
  const lastCheckInDate = new Date(lastCheckIn);
  const diff = now - lastCheckInDate.getTime();
  const day = getMilliseconds({ d: 1, h: 0, m: 0, s: 0 });
  return diff < day;
}

export function userCheckIn(): void {
  const date = new Date();
  localStorage.setItem("lastCheckIn", date.toISOString());
}

// Utility function to check if a string is a valid date
export const isDate = (dateStr: unknown): boolean => {
  if (typeof dateStr !== "string" && typeof dateStr !== "number") return false;
  return !isNaN(new Date(dateStr).getTime());
};

export function extractURLFilters(
  searchParams: URLSearchParams
): AllURLFilterValues {
  // util function to extract all the filters from the URL and return them as a structured object
  const searchText = searchParams.get(KEY_URL_SEARCH);
  const cat = searchParams.get(KEY_URL_CATEGORY);
  const catArr = cat?.split(",");
  const catArrNums = catArr?.map(Number).filter((n) => !isNaN(n));
  const category = catArrNums
    ? catArrNums.length > 0
      ? catArrNums
      : null
    : null;

  const priceMinString = searchParams.get(KEY_URL_PRICE_MIN);
  const priceMin = priceMinString
    ? isNaN(parseFloat(priceMinString))
      ? null
      : parseFloat(priceMinString)
    : null;

  const priceMaxString = searchParams.get(KEY_URL_PRICE_MAX);
  const priceMax = priceMaxString
    ? isNaN(parseFloat(priceMaxString))
      ? null
      : parseFloat(priceMaxString)
    : null;

  const avail = searchParams.get(KEY_URL_AVAILABLE);
  const availArr = avail?.split(",");
  const availArrNums = availArr?.map(Number).filter((n) => !isNaN(n));
  const available = availArrNums
    ? availArrNums.length > 0
      ? availArrNums
      : null
    : null;

  const rating = searchParams.get(KEY_URL_RATING);
  const ratingArr = rating?.split(",");
  const ratingArrNums = ratingArr?.map(Number).filter((n) => !isNaN(n));
  const ratingNums = ratingArrNums
    ? ratingArrNums.length > 0
      ? ratingArrNums
      : null
    : null;

  const bidt = searchParams.get(KEY_URL_BID_TYPE);
  const newBidType = bidt ? bidt.split(",") : null;

  const stat = searchParams.get(KEY_URL_STATUS);
  const newStatus = stat ? stat.split(",") : null;

  const dateMinString = searchParams.get(KEY_URL_DATE_MIN);
  const dateMin = dateMinString
    ? isNaN(parseFloat(dateMinString))
      ? null
      : parseFloat(dateMinString)
    : null;

  const dateMaxString = searchParams.get(KEY_URL_DATE_MAX);
  const dateMax = dateMaxString
    ? isNaN(parseFloat(dateMaxString))
      ? null
      : parseFloat(dateMaxString)
    : null;

  const auctionID = searchParams.get(KEY_URL_AUCTION_ID);
  const auctionIDArr = auctionID?.split(",");
  const auctionIDNums = auctionIDArr
    ? auctionIDArr.map(Number).filter((n) => !isNaN(n))
    : null;

  const daysLeft = searchParams.get(KEY_URL_DAYS_LEFT);
  const daysLeftArr = daysLeft?.split(",");
  const daysLeftNums = daysLeftArr
    ? daysLeftArr.map(Number).filter((n) => !isNaN(n))
    : null;

  const paid = searchParams.get(KEY_URL_PAID);
  const paidArr = paid ? paid.split(",") : null;

  const courier = searchParams.get(KEY_URL_COURIER);
  const courierArr = courier ? courier.split(",") : null;

  const tracking = searchParams.get(KEY_URL_TRACKING);
  const trackingArr = tracking ? tracking.split(",") : null;

  const wonB = searchParams.get(KEY_URL_WON_BY);
  const newWonBy = wonB ? wonB.split(",") : null;

  const urlSortString = searchParams.get(KEY_URL_SORT) || KEY_SORT_RELEVANCE;
  let sort: AllURLFilterValues["sort"] = "rel";
  // check sort is valid
  if (
    urlSortString === KEY_SORT_NEWEST ||
    urlSortString === KEY_SORT_PRICE_ASC ||
    urlSortString === KEY_SORT_PRICE_DESC ||
    urlSortString === KEY_SORT_RATING ||
    urlSortString === KEY_SORT_RELEVANCE
  ) {
    sort = urlSortString;
  } else {
    sort = KEY_SORT_RELEVANCE;
  }

  return {
    searchText,
    category,
    priceMin,
    priceMax,
    available,
    rating: ratingNums,
    bidType: newBidType,
    status: newStatus,
    dateMin,
    dateMax,
    auctionID: auctionIDNums,
    daysLeft: daysLeftNums,
    paid: paidArr,
    courier: courierArr,
    tracking: trackingArr,
    wonBy: newWonBy,
    sort: sort
  };
}

export function filtersHaveChanged(
  oldFilters: AllURLFilterValues,
  newFilters: AllURLFilterValues
): boolean {
  // util function to check if the filters have changed
  return JSON.stringify(oldFilters) !== JSON.stringify(newFilters);
}

export function utilLogOut() {
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("lastCheckedInDate");
  localStorage.removeItem("profileImage");
  removeUUIDChat();
  window.location.href = "/";
}

// remove user chat data
function removeUUIDChat() {
  const lsData = localStorage.getItem(AI_CHAT_LOCAL_STORAGE_KEY);
  if (lsData) {
    const jsonData = JSON.parse(lsData);
    if (jsonData && jsonData.user_data) {
      jsonData.user_data = {
        nickname: "",
        uuid: null,
        timestamp: 0,
        bidsPlaced: 0,
        referrals: 0,
        bidsBought: false
      };
      localStorage.setItem(AI_CHAT_LOCAL_STORAGE_KEY, JSON.stringify(jsonData));
    }
  }
}

export function formatShortDate(
  date: string | number | null | Date,
  noDate?: string
): string {
  // create a sting formatted as "dd mmm yyyy"
  if (!date) return noDate || "";
  const d = new Date(date);
  const day = d.getDate();
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear();

  return `${day} ${month} ${year}`;
}

// get REACT_APP_EMAIL_SUPPORT from .env
export function getSupportEmail(): string {
  return process.env.REACT_APP_EMAIL_SUPPORT || "";
}

// #region SOCIAL_MEDIA
export function goToFacebook() {
  // REACT_APP_FACEBOOK_PAGE
  const url = process.env.REACT_APP_FACEBOOK_PAGE;
  if (url) {
    window.open(url, "_blank");
  }
}

export function goToInstagram() {
  // REACT_APP_INSTAGRAM_PAGE
  const url = process.env.REACT_APP_INSTAGRAM_PAGE;
  if (url) {
    window.open(url, "_blank");
  }
}

export function goToTwitter() {
  // REACT_APP_TWITTER_PAGE
  const url = process.env.REACT_APP_TWITTER_PAGE;
  if (url) {
    window.open(url, "_blank");
  }
}

export function goToYoutube() {
  // REACT_APP_YOUTUBE_PAGE
  const url = process.env.REACT_APP_YOUTUBE_PAGE;
  if (url) {
    window.open(url, "_blank");
  }
}
// #endregion SOCIAL_MEDIA

export function calcAuctionEndTime({
  lastPlacedBidTime,
  auctionTimer
}: {
  lastPlacedBidTime: string | Date;
  auctionTimer: number;
}): number {
  const lastPlacedBidTimeDate = new Date(lastPlacedBidTime);
  const auctionEndTime = new Date(
    lastPlacedBidTimeDate.getTime() + auctionTimer * 1000
  ).getTime();
  return auctionEndTime;
}

export function calcAuctionTimeRemaining({
  auctionEndTime,
  now,
  auctionTimer
}: {
  auctionEndTime: number;
  now: Date;
  auctionTimer: number;
}): number {
  const timeRem = Math.min(
    auctionTimer,
    Math.floor((auctionEndTime - now.getTime()) / 1000)
  );
  return timeRem;
}

export function systemErrorAlertText(): string {
  let htmlContent = "";
  htmlContent += `<div class="mt-5">${i18n.t("If you're encountering any issues, we're here to help!")}</div>`;
  htmlContent += `<div class="mt-2">${i18n.t("Please don't hesitate to contact us directly via our contact form")}.</div>`;

  return htmlContent;
}

export function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
}

export function formatCurrency(amount: number): string {
  // format the amount to a currency string, use spaces as thousands separator. 2 decimal places
  return "R " + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function validatePhoneNumber(phoneNumber: string): boolean {
  // cell allow only numbers, + and - length 10 to 15
  //  remove all spaces
  const phone = phoneNumber.replace(/\s/g, "");
  return /^[0-9+-]{10,15}$/.test(phone);
}

export function formatThousands(
  num: number | string | undefined | null
): string {
  // add a space between each thousand
  // console.log("num", num);
  if (num === undefined || num === null || num === "") return "";
  const numStr = num.toString();
  const parts = numStr.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return parts.join(".");
}

export function createTranslatedRewardString(reward: NewRewardPopup["type"]) {
  let rewardString = "";
  switch (reward) {
    case "Daily Login":
      rewardString = `${i18n.t("Welcome Back!")}`;
      break;
    case "Profile Update":
      rewardString = `${i18n.t("Profile Complete!")}`;
      break;
    case "Referral-Signup":
      rewardString = `${i18n.t("Friend Signup!")}`;
      break;
    case "Share-Signup":
      rewardString = `${i18n.t("Welcome Bonus!")}`;
      break;
    case "Referral-First Purchase":
      rewardString = `${i18n.t("Friend's First Blitz Bought!")}`;
      break;
    case "Share-First Purchase":
      rewardString = `${i18n.t("First Blitz Bought!")}`;
      break;
    case "Referral-First Win":
      rewardString = `${i18n.t("Friend's Auction Win")}`;
      break;
    case "Share-First Win":
      rewardString = `${i18n.t("First Auction Win!")}`;
      break;
    case "coupon":
      rewardString = `${i18n.t("Coupon Redeemed!")}`;
      break;

    default:
      console.error("Reward type not found", reward);
      rewardString = reward;
      break;
  }

  return rewardString;
}

export function generateUuid(): string | null {
  try {
    const newUUID = uuidv4();
    if (newUUID && typeof newUUID === "string") {
      console.log("GENERATED uuid", newUUID);
      return newUUID;
    } else {
      console.error("Error Generating UUID");
      return null;
    }
  } catch (error) {
    console.error("Error Generating UUID");
    return null;
  }
}

export function retrieveDeviceId(): string | null {
  const device_ID = localStorage.getItem(DEVICE_ID_KEY);
  if (!device_ID) {
    const newDeviceID = generateUuid();
    localStorage.setItem(DEVICE_ID_KEY, newDeviceID || "");
    return newDeviceID;
  } else {
    return device_ID;
  }
}

export async function checkExclude(): Promise<boolean> {
  try {
    if (!process.env.REACT_APP_EXCLUDE) {
      console.error("MISSING EXCLUDE IP ENV VALUE");
    }

    const response = await fetch("https://api.ipify.org?format=json");
    if (!response.ok) {
      console.error("Network response was not ok for IP");
    }

    const data = await response.json();
    if (data) {
      const userIp = data.ip;

      if (userIp) {
        if (
          process.env.REACT_APP_EXCLUDE?.trim().toLowerCase() ===
          userIp.trim().toLowerCase()
        ) {
          return true;
        }
      }
    }

    return false;
    // // Check if the IP is excluded
  } catch (error) {
    console.error("Error fetching IP address:", error);
    return false;
  }
}
